import { Injectable, inject, signal, computed } from '@angular/core';
import { environment } from '@env/environment';
import { MiscModel } from '@models/misc-model';
import { StoreService } from '@services/store.service';
import { UserService } from '@services/user.service';
import { firstValueFrom } from 'rxjs';

export interface MiscModelState {
  miscModels: MiscModel[] | undefined;
  lastFetchTime: number | undefined;
}

@Injectable({
  providedIn: 'root'
})
export class MiscModelsService {
  private storeService = inject(StoreService);
  private userService = inject(UserService);
  
  private stockKeepingUnitsEndpoint: string = 'StockKeepingUnits';
  
  private miscModelState = signal<MiscModelState>({
    miscModels: undefined,
    lastFetchTime: undefined
  });
  
  private miscModelsComputed = computed(async () => {
    if (!this.miscModelState().miscModels || this.miscModelState().miscModels?.length === 0) {
      const result = await this.storeService.getOrSetFromStore<Array<MiscModel>>(`${environment.dataUrl}/${this.stockKeepingUnitsEndpoint}`);
      const mappedResult = result?.map((x) => MiscModel.createCopy(x as MiscModel))
        .filter(model => model !== null) as MiscModel[];
      
      this.setMiscModels(mappedResult || []);
    }
    
    return this.miscModelState().miscModels;
  });
  
  private setMiscModels(miscModels: MiscModel[]) {
    this.miscModelState.set({
      ...this.miscModelState(),
      miscModels: miscModels,
      lastFetchTime: Date.now()
    });
  }
  
  /**
   * Retrieves misc models, optionally including items with zero maximum quantity
   */
  public async getMiscModels(includeItemsWithZeroMaxQuantity: boolean = false): Promise<Array<MiscModel>> {
    const miscModels = await this.miscModelsComputed();
    if (!miscModels) return [];
    
    // Create a copy to avoid mutating the stored data
    let filteredResult = [...miscModels];
    const user = await this.userService.GetUser();

    if (!includeItemsWithZeroMaxQuantity) {
      // casting skyldes at vi fra API'et returnerer en string, men typen her er et number.
      // "" fra API'et betyder "ingen begrænsning", men "0" betyder at den ikke skal vises
      filteredResult = filteredResult?.filter(t => (t?.MaximumOrderQty as unknown as string) !== "0");
    }

    // *** If a model has web resource restraints (if there is any data in the WebResourceCodes list),
    // *** the corresponding web resources must be present in the users list of of web resource claims

    let resultFilteredByWebResources: MiscModel[] = [];
    if (filteredResult)
      for (const m of filteredResult) {
        if (m && m.WebResourceCodes && m.WebResourceCodes.length > 0) {
          let commonWebResources = m.WebResourceCodes.some(c => user?.WebResources?.includes(c));

          if (commonWebResources) {
            resultFilteredByWebResources.push(m);
          }
        }
        else {
          m && resultFilteredByWebResources.push(m);
        }
      }

    return resultFilteredByWebResources;
  }
  
  /**
   * Retrieves a specific case misc model based on item number and variant code
   */
  public async getCaseMiscModel(caseItemNo: string, caseItemVariantCode: string): Promise<MiscModel | undefined> {
    const miscModels = await this.getMiscModels(true);
    const miscCase = miscModels.find(t => t.EcommerceNo == caseItemNo && t.EcommerceVariantCode == caseItemVariantCode);

    return miscCase;
  }
} 